import clsx from 'clsx';
import type { ComponentType } from 'react';
import type { ArticleCardProps } from '../../../components/cards/article/ArticleCard';
import { ArticleCard } from '../../../components/cards/article/ArticleCard';
import { Button } from '../../../components/ui/button/Button';
import type { StoreProductWishlistComponent } from '../../../types.d/storeProduct';
import styles from './FeaturedArticles.module.scss';

export type FeaturedArticlesProps = {
  title?: string;
  buttonLabel?: string;
  buttonLink?: string;
  newTab?: boolean;
  featuredArticle?: ArticleCardProps;
  articles?: Array<ArticleCardProps>;
  titleClassName?: string;
  articleGridClassName?: string;
  wishlistComponent?: ComponentType<StoreProductWishlistComponent>;
};

/**
 * The Featured Articles section component.
 * @param {Object} props - The component props.
 * @returns {ReactNode}
 */
export const FeaturedArticles = ({
  title,
  buttonLabel,
  buttonLink,
  newTab,
  featuredArticle,
  articles = [],
  titleClassName,
  articleGridClassName,
  wishlistComponent,
}: FeaturedArticlesProps) => {
  const hasButton = !!buttonLabel && !!buttonLink;

  return (
    <section className={styles.featuredArticles}>
      {(title || hasButton) && (
        <div className={styles.featuredArticles__headerContainer}>
          <div className={styles.featuredArticles__header}>
            {title && (
              <h2
                className={clsx(
                  titleClassName,
                  'text-blog-heading-h2-mobile text-blog-heading-h1-desktop'
                )}
              >
                {title}
              </h2>
            )}

            {hasButton && (
              <Button
                className={styles.featuredArticles__button}
                href={buttonLink}
                modifiers={'text'}
                target={newTab ? '_blank' : undefined}
              >
                {buttonLabel}
              </Button>
            )}
          </div>
        </div>
      )}

      {featuredArticle && (
        <div className={styles.featuredArticles__featured}>
          <ArticleCard
            {...featuredArticle}
            featured={true}
            wishlistComponent={wishlistComponent}
          />
        </div>
      )}

      {articles.length >= 1 && (
        <div className={styles.featuredArticles__articles}>
          <div className={styles.featuredArticles__grid}>
            {articles.map((article, index) => (
              <div
                key={index}
                className={clsx(
                  styles.featuredArticles__article,
                  articleGridClassName
                )}
              >
                <ArticleCard
                  {...article}
                  wishlistComponent={wishlistComponent}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
